import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/_router";
import { actions, desktop, runtime, scan } from "@/controller";
import GlobalDictationOnboarding from "@/components/GlobalDictationOnboarding";
import {
  CardIcon,
  ScanIcon,
  MicrophoneIcon,
  UsersIcon,
  DownloadIcon,
  ChevronRightIcon,
} from "@/components/Icons";
import NativeHomeJourney from "@/components/NativeHomeJourney";
import { hasAccountIdentity } from "@/auth-identity";
import { getDesktopHomeJourney } from "@/dictation-interactions";
import { useSharedDesktopHost } from "@/models/desktop-host-context";
import { getAppsWebUrl } from "@/product-links";
import { logClientEvent } from "@/telemetry";
import { useAuth } from "@/models/useAuth";
import { useUISlice } from "@/models/useUISlice";
import { useUpload } from "@/models/useUpload";
import { useAppStore } from "@/controller";
import { version } from "../../../package.json";
import { toast } from "react-hot-toast";
import { createActionError, getUserErrorMessage } from "@/client-error";

export const useHomeBootstrap = (
  setIsInitialLoading: (loading: boolean) => void,
  allowWebGuest = false,
) => {
  const { push } = useRouter();
  // Session expiry can flip isNewSession back to true while /home is already
  // mounted (native token replacement navigates here without a remount), so
  // the bootstrap must re-run on that signal, not only on mount.
  const isNewSession = useUISlice((state) => state.isNewSession);
  // The bootstrap flips isNewSession to false while its fetches are still in
  // flight, which re-runs this effect. Only the run that started the fetches
  // may lift the loader — otherwise the empty state flashes before the
  // transcript list lands.
  const bootstrapping = useRef(false);

  useEffect(() => {
    if (!isNewSession) {
      if (!bootstrapping.current) setIsInitialLoading(false);
      return;
    }
    if (bootstrapping.current) return;

    const finish = () => {
      bootstrapping.current = false;
      setIsInitialLoading(false);
    };

    if (!runtime.isNative && !desktop.isAvailable && !allowWebGuest) {
      if (!useAuth.isAuthenticated()) {
        push("/auth");
        setIsInitialLoading(false);
        return;
      }
      bootstrapping.current = true;
      useUISlice.getState().setIsNewSession(false);
      void Promise.all([
        actions.user.profile(),
        actions.user.remoteConfig({ platform: runtime.platform, version }),
      ]).finally(finish);
    } else {
      bootstrapping.current = true;
      void useAuth
        .signInAsGuest({
          callback: async () => {
            useUISlice.getState().setIsNewSession(false);
            try {
              await Promise.all([
                actions.user.profile(),
                actions.user.remoteConfig({
                  platform: runtime.platform,
                  version,
                }),
                runtime.isNative
                  ? actions.subscription.configureRevenueCat()
                  : undefined,
              ]);
            } finally {
              finish();
            }
          },
        })
        .finally(() => {
          // Guest sign-in can fail (API unreachable, rejected device session).
          // Surface the home screen instead of an infinite loading state.
          if (!useAuth.isAuthenticated()) finish();
        });
    }
  }, [allowWebGuest, isNewSession, push, setIsInitialLoading]);
};

export const HomeNextStep = ({
  onDictationSurfaceChange,
}: {
  onDictationSurfaceChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const host = useSharedDesktopHost();
  const userToken = useAppStore((state) => state.userToken);
  const connectedClients = useAppStore(
    (state) => state.userData?.connectedClients,
  );
  const intent = useAppStore((state) => state.onboardingIntent);
  const hasCompletedOnboarding = useAppStore(
    (state) => state.hasCompletedOnboarding,
  );
  const recordingStatus = useUISlice((state) => state.desktopRecordingStatus);
  if (runtime.platform === "android" || runtime.platform === "ios")
    return (
      <NativeHomeJourney onDictationSurfaceChange={onDictationSurfaceChange} />
    );
  // Wait for real host capabilities, so sign-in never flashes before setup.
  if (desktop.isAvailable && !host.permissions) return null;
  const needsAccount = !hasAccountIdentity(useAuth.currentAuthUser(userToken));
  const journey = desktop.isAvailable
    ? getDesktopHomeJourney({
        intent,
        dictationReady:
          host.permissions?.microphone === "granted" &&
          host.permissions.accessibility === "granted",
        recordingReady:
          host.permissions?.microphone === "granted" &&
          host.permissions.systemAudio === "granted",
        recordingSupported: host.permissions?.systemAudio !== "unsupported",
        hasAccount: !needsAccount,
      })
    : undefined;
  if (desktop.isAvailable && recordingStatus)
    return (
      <section
        data-testid="desktop-recording-status"
        role="status"
        className="glass-tile mb-3 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm"
      >
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-[var(--destructive)]"
        />
        <span>{t(`desktopSetup.capture_${recordingStatus}`)}</span>
      </section>
    );
  const needsDesktop =
    !runtime.isNative &&
    !desktop.isAvailable &&
    !connectedClients?.some(
      (client) =>
        client === "macDesktop" ||
        client === "windowsDesktop" ||
        client === "chromeExtension",
    );
  const step =
    journey?.nextStep ??
    (!desktop.isAvailable
      ? needsAccount
        ? "account"
        : needsDesktop
          ? "desktop"
          : undefined
      : undefined);
  if (!step) return null;
  const setup = step === "dictate" || step === "record";
  const title = t(
    setup
      ? `desktopSetup.story.setup${step === "record" ? "Record" : "Dictate"}`
      : step === "account"
        ? "settings.signInToSync"
        : "settings.useOnComputer",
  );
  const subtitle = t(
    setup
      ? step === "dictate"
        ? "desktopSetup.permissionsBody"
        : "desktopSetup.next_record"
      : step === "account"
        ? "settings.signInSubtitle"
        : "settings.dictationOnComputer",
  );
  const Icon = setup
    ? MicrophoneIcon
    : step === "account"
      ? UsersIcon
      : DownloadIcon;
  return (
    <section
      data-testid="home-next-step"
      data-next-step={step}
      aria-label={title}
      className="relative mb-3"
    >
      <button
        type="button"
        data-action-id="home.journey.resume"
        onClick={() => {
          if (setup) {
            logClientEvent("dictation.onboardingOpened", {
              source: "home-journey",
            });
            useUISlice.setState({ desktopDictationSetupRequested: step });
          } else if (step === "account") useAuth.signIn({ callback: () => {} });
          else window.open(getAppsWebUrl(), "_blank", "noopener,noreferrer");
        }}
        className="glass-tile flex min-h-16 w-full items-center gap-3 rounded-2xl px-3.5 py-2.5 text-start transition-colors active:bg-[var(--glass-bg-active)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)] text-[var(--accent)]">
          <Icon size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold text-[var(--text-primary)]">
            {title}
          </span>
          <span className="mt-0.5 block text-xs text-[var(--text-secondary)]">
            {subtitle}
          </span>
          {journey && !hasCompletedOnboarding ? (
            <span className="mt-2 flex items-center gap-2 text-[11px] text-[var(--text-tertiary)]">
              <span>
                {t("desktopSetup.progress", {
                  count: journey.completedCount,
                  total: journey.totalCount,
                })}
              </span>
              <span className="flex gap-1" aria-hidden="true">
                {Array.from({ length: journey.totalCount }, (_, index) => (
                  <span
                    key={index}
                    className={`h-1 w-5 rounded-full ${index < journey.completedCount ? "bg-[var(--accent)]" : "bg-[var(--fill-2)]"}`}
                  />
                ))}
              </span>
            </span>
          ) : null}
        </span>
        <ChevronRightIcon
          size={18}
          className="shrink-0 text-[var(--text-tertiary)] rtl:rotate-180"
        />
      </button>
    </section>
  );
};

export const HomeBillingButton = ({
  label,
  variant,
}: {
  label: string;
  variant: "compact" | "desktop";
}) => {
  const { t } = useTranslation();
  const managementProvider = useAppStore(
    (state) => state.subscriptionManagementProvider,
  );
  if (runtime.isNative || !managementProvider) return null;

  return (
    <button
      data-action-id="home.subscription.manage"
      onClick={async () => {
        const result = await actions.subscription.manage();
        if ("error" in result) {
          toast.error(t("useAuth.signInFailed"));
          return;
        }
        window.open(result.value, "_self");
      }}
      className={
        variant === "desktop"
          ? "h-11 pl-4 pr-3 rounded-full flex items-center justify-center hover:bg-[var(--fill-1)] active:scale-95 transition-all relative mx-1 text-[var(--text-primary)]"
          : "h-10 pl-4 pr-3 rounded-full flex items-center justify-center active:scale-95 transition-all glass-tile relative"
      }
    >
      <CardIcon size={16} className="text-[var(--text-primary)] mr-1.5" />
      <span className="text-[var(--text-primary)] font-bold text-xs tracking-wider uppercase">
        {label}
      </span>
    </button>
  );
};

const NativeBackBoundary = ({
  dictationSurfaceOpen,
  settingsOpen,
  isSelectionMode,
  exitSelectionMode,
}: {
  dictationSurfaceOpen: boolean;
  settingsOpen: boolean;
  isSelectionMode: boolean;
  exitSelectionMode: () => void;
}) => {
  useEffect(() => {
    if (!runtime.isNative) return;
    let disposed = false;
    let attached: { remove: () => Promise<void> } | undefined;
    void runtime.app
      .onBackButton(() => {
        const user = useAppStore.getState();
        if (!user.hasCompletedOnboarding && !user.isSubscribed) return;
        if (dictationSurfaceOpen) return;
        // The settings slide-over owns back handling while presented.
        if (settingsOpen) return;
        if (isSelectionMode) {
          exitSelectionMode();
          return;
        }

        const ui = useUISlice.getState();
        if (ui.alertPromptSignIn) {
          // Forced sign-in (hideDismissIcon) must stay up; otherwise back
          // closes the dialog like a backdrop tap instead of minimizing.
          if (!ui.alertPromptSignIn.hideDismissIcon) {
            ui.alertPromptSignIn.onDismiss?.();
            ui.setAlertPromptSignIn(undefined);
          }
          return;
        }
        if (ui.alertModal) {
          if (!ui.alertModal.hideDismissIcon) {
            if (ui.alertModal.onDismiss) ui.alertModal.onDismiss();
            else ui.setAlertModal(undefined);
          }
          return;
        }
        if (ui.alertPrompt) {
          ui.setAlertPrompt(undefined);
          return;
        }
        if (ui.alertLanguagePicker) {
          ui.setAlertLanguagePicker(undefined);
          return;
        }
        if (ui.alertWidgetCreateTranscriptFromFileImport) {
          ui.setAlertCreateWidgetTranscriptFromFileImport(undefined);
          return;
        }
        if (ui.alertWidgetSearch) {
          ui.setAlertWidgetSearch(undefined);
          return;
        }
        if (ui.paywallVisible) return;
        void runtime.app.minimize();
      })
      .then((listener) => {
        if (disposed) void listener.remove();
        else attached = listener;
      });

    return () => {
      disposed = true;
      void attached?.remove();
    };
  }, [dictationSurfaceOpen, exitSelectionMode, isSelectionMode, settingsOpen]);

  return null;
};

export const HomeNativeFeatures = ({
  isSelectionMode,
  exitSelectionMode,
  settingsOpen,
  journeyDictationSurfaceOpen,
}: {
  isSelectionMode: boolean;
  exitSelectionMode: () => void;
  settingsOpen: boolean;
  journeyDictationSurfaceOpen: boolean;
}) => {
  const [homeOnboardingOpen, setHomeOnboardingOpen] = useState(false);

  useEffect(() => {
    if (!runtime.isNative) return;
    const openOnboarding = () => setHomeOnboardingOpen(true);
    // action-id: lifecycle.dictation.onboardingOpen
    window.addEventListener("openGlobalDictationOnboarding", openOnboarding);
    return () =>
      window.removeEventListener(
        "openGlobalDictationOnboarding",
        openOnboarding,
      );
  }, []);

  if (!runtime.isNative) return null;
  const dictationSurfaceOpen = journeyDictationSurfaceOpen || homeOnboardingOpen;

  return (
    <>
      <NativeBackBoundary
        dictationSurfaceOpen={dictationSurfaceOpen}
        settingsOpen={settingsOpen}
        isSelectionMode={isSelectionMode}
        exitSelectionMode={exitSelectionMode}
      />
      <GlobalDictationOnboarding
        open={homeOnboardingOpen}
        onClose={() => setHomeOnboardingOpen(false)}
      />
    </>
  );
};

export const NativeHomeScanButton = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { getUploadedFileUrl } = useUpload();
  if (!runtime.isNative) return null;

  const showUploadError = (error: unknown) => {
    const message = getUserErrorMessage(error, {
      generic: t("home.uploadError"),
      networkUnavailable: t("alertLargeFileUpload.errorRetriesExhausted"),
    });
    if (message) toast.error(message);
  };

  const handleScan = async () => {
    try {
      const file = await scan.capture();
      if (!file) return;
      await getUploadedFileUrl({
        file,
        onSuccess: async (originalFileUrl) => {
          const created = await actions.transcript.create({
            type: "take_photo_or_import_image_or_pdf",
            fileName: file.name,
            language:
              useAppStore.getState().userData?.preferences
                ?.transcriptionLanguage || "en",
            originalFileUrl,
          });
          if ("error" in created) throw createActionError(created.error);
          router.push({
            pathname: "/transcript",
            search: `?${new URLSearchParams({ id: created.value.id })}`,
          });
        },
        onError: showUploadError,
      });
    } catch (error) {
      showUploadError(error);
    }
  };

  return (
    <button
      data-action-id="home.scan"
      onClick={() => void handleScan()}
      className="flex flex-col items-center gap-1.5 group"
    >
      <div className="glass w-16 h-16 rounded-full active:bg-[var(--glass-bg-active)] backdrop-blur-md flex items-center justify-center active:scale-90 transition-all duration-300">
        <ScanIcon
          className="text-[var(--text-primary)] opacity-80 group-active:opacity-100 transition-colors"
          size={24}
        />
      </div>
      <span className="text-[11px] font-medium text-[var(--text-tertiary)] group-active:text-[var(--text-secondary)] transition-colors">
        {t("home.scan")}
      </span>
    </button>
  );
};
